import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { apiClient, handleApiError } from '@/config/api';
import { GymLocation, MyClassesResponse, StaffClassesResponse } from '@/types/api';

/**
 * Admin "Staff" view of the Classes tab: who's teaching at a gym on a day /
 * in an hour (GET /classes/staff), and one staff member's day
 * (GET /classes/staff/{id}). The backend 403s non-admins.
 *
 * ⚠️ Registered in BOTH store/index.ts and store/index.web.ts.
 */
export interface StaffFilters {
  /** null = the admin's home gym (server decides). */
  location: GymLocation | null;
  /** yyyy-MM-dd, or null = today. */
  date: string | null;
  /** 0–23, or null = all day. */
  hour: number | null;
  /**
   * Client-only: the admin is watching "today, this hour" (the default), so a
   * refresh moves to the new current hour. Not sent to the server.
   */
  followNow?: boolean;
}

interface StaffClassesState {
  list: StaffClassesResponse | null;
  /** The last requested filters — responses for any other request are dropped. */
  filters: StaffFilters | null;
  isLoading: boolean;
  error: string | null;
  /** One staff member's day, keyed by the request that produced it. */
  member: MyClassesResponse | null;
  memberRequest: string | null;
  memberLoading: boolean;
  memberError: string | null;
}

const initialState: StaffClassesState = {
  list: null,
  filters: null,
  isLoading: false,
  error: null,
  member: null,
  memberRequest: null,
  memberLoading: false,
  memberError: null,
};

const sameFilters = (a: StaffFilters | null, b: StaffFilters) =>
  !!a && a.location === b.location && a.date === b.date && a.hour === b.hour;

export const getStaffClasses = createAsyncThunk(
  'staffClasses/fetch',
  async (filters: StaffFilters, { rejectWithValue }) => {
    const q = new URLSearchParams();
    if (filters.location) q.set('location', filters.location);
    if (filters.date) q.set('date', filters.date);
    if (filters.hour !== null) q.set('hour', String(filters.hour));
    const query = q.toString();
    try {
      return await apiClient.get<StaffClassesResponse>(`/classes/staff${query ? `?${query}` : ''}`);
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

const memberKey = (a: { employeeId: number; date: string | null }) =>
  `${a.employeeId}|${a.date ?? ''}`;

export const getStaffMemberClasses = createAsyncThunk(
  'staffClasses/fetchMember',
  async (arg: { employeeId: number; date: string | null }, { rejectWithValue }) => {
    try {
      const query = arg.date ? `?date=${encodeURIComponent(arg.date)}` : '';
      return await apiClient.get<MyClassesResponse>(`/classes/staff/${arg.employeeId}${query}`);
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

const staffClassesSlice = createSlice({
  name: 'staffClasses',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getStaffClasses.pending, (state, action) => {
        state.isLoading = true;
        state.error = null;
        state.filters = action.meta.arg;
      })
      // Last request wins: drop responses for filters the admin has moved past.
      .addCase(getStaffClasses.fulfilled, (state, action) => {
        if (!sameFilters(state.filters, action.meta.arg)) return;
        state.isLoading = false;
        state.list = action.payload;
      })
      .addCase(getStaffClasses.rejected, (state, action) => {
        if (!sameFilters(state.filters, action.meta.arg)) return;
        state.isLoading = false;
        state.error = (action.payload as string) ?? 'Could not load staff classes';
      })
      .addCase(getStaffMemberClasses.pending, (state, action) => {
        state.memberLoading = true;
        state.memberError = null;
        // Never show the previous staff member's day under a new name.
        const prevEmployee = state.memberRequest?.split('|')[0];
        if (prevEmployee !== String(action.meta.arg.employeeId)) state.member = null;
        state.memberRequest = memberKey(action.meta.arg);
      })
      .addCase(getStaffMemberClasses.fulfilled, (state, action) => {
        if (state.memberRequest !== memberKey(action.meta.arg)) return;
        state.memberLoading = false;
        state.member = action.payload;
      })
      .addCase(getStaffMemberClasses.rejected, (state, action) => {
        if (state.memberRequest !== memberKey(action.meta.arg)) return;
        state.memberLoading = false;
        state.memberError = (action.payload as string) ?? 'Could not load classes';
      });
  },
});

export default staffClassesSlice.reducer;
